// Tracer: tightened Zod for the Client row. Every other table currently uses
// `rowBaseSchema` (passthrough). This file proves that tightening is possible
// per-table when wanted, mirroring the TS interface in src/data/types.ts.

import { z } from 'zod';

export const accountTypeSchema = z.enum([
    'GIA',
    'ISA',
    'SIPP',
    'JISA',
    'Joint GIA',
    'Trust',
    'Charity',
    'Investment Company',
]);

export const clientSegmentSchema = z.enum([
    'UHNW',
    'HNW',
    'Standard',
    'Trust',
    'Entity',
    'Charity',
    'IFA-introduced',
]);

export const investmentMandateSchema = z.enum([
    'Growth',
    'Balanced Growth',
    'Balanced',
    'Cautious Growth',
    'Cautious',
    'Income',
    'Ethical',
]);

export const serviceTypeSchema = z.enum([
    'Discretionary',
    'Advisory-Managed',
    'Advisory',
    'Execution Only',
]);

export const complianceStatusSchema = z.enum(['Current', 'Review Approaching', 'Overdue']);
export const riskRatingSchema = z.enum(['High', 'Medium', 'Standard']);

export const accountSchema = z.object({
    id: z.string(),
    reference: z.string(),
    type: accountTypeSchema,
    custodian: z.string(),
    value: z.number(),
});

// The minimal `{ id, name }` client reference several view contracts attach so
// the page can resolve a clientId to a display name without a second round-trip
// (call-intelligence, fair-value, suitability, desk-pages). One named schema,
// imported by all of them, rather than four anonymous re-declarations.
export const clientStubSchema = z.object({
    id: z.string(),
    name: z.string(),
});
export type ClientStub = z.infer<typeof clientStubSchema>;

export const clientRowSchema = z.object({
    id: z.string(),
    name: z.string(),
    totalAUM: z.number(),
    accounts: z.array(accountSchema),
    custodian: z.string(),
    investmentManager: z.string(),
    mandate: investmentMandateSchema,
    serviceType: serviceTypeSchema,
    clientSince: z.number(),
    kycDueDescription: z.string(),
    /** ISO date string (YYYY-MM-DD) — fact-find expiry date (last completed + 12 months). */
    factFindExpiresAt: z.string(),
    segment: clientSegmentSchema,
    riskRating: riskRatingSchema,
    complianceStatus: complianceStatusSchema,
    lei: z.string().optional(),
    clientType: z.string(),
    introducingIFA: z.string().optional(),
    feeSplit: z.string().optional(),
    performanceYTD: z.number(),
    performance1Y: z.number(),
    allocationSummary: z.record(z.string(), z.number()).optional(),
    lastContactDate: z.string().optional(),
});

export type ClientRow = z.infer<typeof clientRowSchema>;
